import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fetches a representative product image for each subcategory name.
 * 1. Direct match by category column
 * 2. Match via product_categories table
 * 3. Fallback: for remaining subcategories, use products from the parent category
 */
async function fetchSubcategoryImages(
  subcategoryNames: string[],
  parentCategory?: string
): Promise<Map<string, string>> {
  if (subcategoryNames.length === 0) return new Map();

  const result = new Map<string, string>();

  // 1. Direct match: products whose `category` column matches a subcategory name
  const { data: directProducts } = await supabase
    .from("products")
    .select("category, image_url")
    .eq("is_published", true).gt("stock", 0)
    .eq("is_deleted", false)
    .in("category", subcategoryNames)
    .not("image_url", "eq", "")
    .order("sold_count", { ascending: false })
    .limit(subcategoryNames.length * 3);

  for (const p of directProducts || []) {
    if (!result.has(p.category) && p.image_url?.startsWith("http")) {
      result.set(p.category, p.image_url);
    }
  }

  // 2. For remaining names, check product_categories table
  let remaining = subcategoryNames.filter(n => !result.has(n));
  if (remaining.length > 0) {
    const { data: mappings } = await supabase
      .from("product_categories")
      .select("product_id, subcategory")
      .in("subcategory", remaining)
      .limit(remaining.length * 3);

    if (mappings && mappings.length > 0) {
      const productIds = [...new Set(mappings.map(m => m.product_id))];
      const { data: products } = await supabase
        .from("products")
        .select("id, image_url")
        .eq("is_published", true).gt("stock", 0)
        .eq("is_deleted", false)
        .not("image_url", "eq", "")
        .in("id", productIds.slice(0, 50));

      const prodMap = new Map((products || []).map(p => [p.id, p.image_url]));

      for (const m of mappings) {
        if (!m.subcategory || result.has(m.subcategory)) continue;
        const img = prodMap.get(m.product_id);
        if (img?.startsWith("http")) result.set(m.subcategory, img);
      }
    }
  }

  // 3. Fallback: for subcategories still without images, grab from parent category
  remaining = subcategoryNames.filter(n => !result.has(n));
  if (remaining.length > 0 && parentCategory) {
    const { data: parentProducts } = await supabase
      .from("products")
      .select("image_url")
      .eq("is_published", true).gt("stock", 0)
      .eq("is_deleted", false)
      .eq("category", parentCategory)
      .not("image_url", "eq", "")
      .order("sold_count", { ascending: false })
      .limit(remaining.length * 2);

    const validImages = (parentProducts || [])
      .map(p => p.image_url)
      .filter(url => url?.startsWith("http"));

    // Distribute parent images across remaining subcategories
    remaining.forEach((name, i) => {
      const img = validImages[i % validImages.length];
      if (img) result.set(name, img);
    });
  }

  return result;
}

export function useSubcategoryImages(subcategoryNames: string[], parentCategory?: string) {
  return useQuery({
    queryKey: ["subcategory-images", subcategoryNames.sort().join(","), parentCategory || ""],
    queryFn: () => fetchSubcategoryImages(subcategoryNames, parentCategory),
    staleTime: 10 * 60 * 1000,
    enabled: subcategoryNames.length > 0,
  });
}
